import { useState, useEffect, useCallback } from 'react';
import { ShippingLineItem } from '../DeShippingTableWrapper';

interface UseLineItemSelectionReturn {
  selectedLineItems: ShippingLineItem[];
  setSelectedLineItems: (items: ShippingLineItem[]) => void;
  selectLineItem: (lineItem: ShippingLineItem) => void;
  deselectLineItem: (lineItemId: number) => void;
  toggleLineItemSelection: (lineItem: ShippingLineItem) => void;
  selectAllUnassigned: () => void;
  clearSelection: () => void;
  getSelectAllState: () => { checked: boolean; indeterminate: boolean };
  selectedUnassignedCount: number;
  unassignedItems: ShippingLineItem[];
}

export const useLineItemSelection = (
  lineItems: ShippingLineItem[], 
  orderId: string | undefined
): UseLineItemSelectionReturn => {
  const [selectedLineItems, setSelectedLineItems] = useState<ShippingLineItem[]>([]);

  // Computed values
  const unassignedItems = lineItems.filter(item => item.fulfillment_status === 'unassigned');
  const selectedUnassignedCount = selectedLineItems.filter(item => 
    item.fulfillment_status === 'unassigned'
  ).length;

  // Selection management functions
  const selectLineItem = useCallback((lineItem: ShippingLineItem) => {
    setSelectedLineItems(prev => {
      const isAlreadySelected = prev.some(item => item.line_item_id === lineItem.line_item_id);
      if (!isAlreadySelected) {
        return [...prev, lineItem];
      }
      return prev;
    });
  }, []);

  const deselectLineItem = useCallback((lineItemId: number) => {
    setSelectedLineItems(prev => 
      prev.filter(item => item.line_item_id !== lineItemId)
    );
  }, []);

  const toggleLineItemSelection = useCallback((lineItem: ShippingLineItem) => {
    const isSelected = selectedLineItems.some(item => item.line_item_id === lineItem.line_item_id);
    if (isSelected) {
      deselectLineItem(lineItem.line_item_id);
    } else {
      selectLineItem(lineItem);
    }
  }, [selectedLineItems, deselectLineItem, selectLineItem]);

  const selectAllUnassigned = useCallback(() => {
    setSelectedLineItems(unassignedItems);
  }, [unassignedItems]);

  const clearSelection = useCallback(() => {
    setSelectedLineItems([]);
  }, []);

  const getSelectAllState = useCallback(() => {
    if (unassignedItems.length === 0) return { checked: false, indeterminate: false };
    
    const selectedUnassignedItems = selectedLineItems.filter(item => 
      item.fulfillment_status === 'unassigned'
    );
    
    if (selectedUnassignedItems.length === 0) {
      return { checked: false, indeterminate: false };
    } else if (selectedUnassignedItems.length === unassignedItems.length) {
      return { checked: true, indeterminate: false };
    } else {
      return { checked: false, indeterminate: true };
    }
  }, [selectedLineItems, unassignedItems]);

  // Clear selections when line items change
  useEffect(() => {
    setSelectedLineItems(prev => 
      prev.filter(selected => 
        lineItems.some(item => item.line_item_id === selected.line_item_id)
      )
    );
  }, [lineItems]);

  // Clear selections when order changes
  useEffect(() => {
    setSelectedLineItems([]);
  }, [orderId]);

  return {
    selectedLineItems,
    setSelectedLineItems,
    selectLineItem,
    deselectLineItem,
    toggleLineItemSelection,
    selectAllUnassigned,
    clearSelection,
    getSelectAllState,
    selectedUnassignedCount,
    unassignedItems
  };
}; 